/**
 * Source catalogue.
 *
 * English is the source language because the brief is written in English and
 * because every other catalogue is checked against this one. It is not a
 * privileged runtime locale -- it is the fallback when a key is missing from a
 * translation, so a half-finished translation shows English for the gaps
 * instead of a blank or a raw key.
 *
 * Rules for anyone adding a string:
 *
 *  - Never build a sentence by concatenating two entries. Translators need to
 *    move the pieces, and in some languages the order is the opposite.
 *  - Anything counted needs `{n, plural, …}`, never `n === 1 ? … : …`. Arabic
 *    has six plural categories; Japanese has one.
 *  - Keep the `TWO ENDS. NO MIDDLE.` tagline out of here entirely. It is part
 *    of the mark and ships in English in every locale.
 */
export const en = {
  // -- app shell -------------------------------------------------------------
  'app.opening': 'Opening the vault…',
  'app.empty.title': 'Choose a conversation',
  'app.empty.body':
    'Your messages are end-to-end encrypted. The server passes sealed envelopes between you — it cannot open them, and neither can anyone who gets access to it.',
  'app.identityAlert.body': 'The identity key for {name} changed. They may have reinstalled the app — or someone may be impersonating them.',
  'app.identityAlert.accept': 'Accept the new key',
  'app.identityAlert.dismiss': 'Ignore',

  // -- onboarding ------------------------------------------------------------
  'onboarding.invited': '{name} invited you.',
  'onboarding.invited.detail': 'After you sign up, your conversation with them will be waiting.',
  'onboarding.intro':
    'End-to-end encrypted messages. Your keys are generated here, on your device, and never leave it.',
  'onboarding.username.label': 'Username',
  'onboarding.username.hint': '3–32 characters, in a single script.',
  'onboarding.displayName.label': 'Display name',
  'onboarding.phone.label': 'Phone number — optional',
  'onboarding.phone.hint':
    'Only so people who already have your number can find you. It is not your account name, it is not how you sign in, and removing it later takes nothing else with it.',
  'onboarding.submit': 'Create account',
  'onboarding.submit.busy': 'Generating keys…',
  'onboarding.warning':
    'There is no password and no recovery. Your identity is a private key stored in this browser — if you clear site data, you will need to sign up again.',
  'onboarding.username.error.too_short': 'At least 3 characters.',
  'onboarding.username.error.too_long': 'At most 32 characters.',
  'onboarding.username.error.bad_start': 'Cannot start with a dot, dash or underscore.',
  'onboarding.username.error.disallowed_character': 'Contains a character that is not allowed.',
  'onboarding.username.error.mixed_script':
    'Mixes two writing systems. Pick one, so your name cannot be imitated.',
  'onboarding.username.error.reserved': 'That name is reserved.',

  // -- sidebar ---------------------------------------------------------------
  'status.online': 'Online',
  'status.connecting': 'Connecting…',
  'status.offline': 'Offline',
  'sidebar.invite': 'Invite friends',
  'sidebar.findByPhone': 'Find by phone number',
  'sidebar.newChat': 'New conversation',
  'sidebar.search': 'Search conversations',
  'sidebar.conversations': 'Conversations',
  'sidebar.empty': 'No conversations yet.',
  'sidebar.noMatches': 'No matches',
  'sidebar.noMessages': 'No messages yet',
  'sidebar.group': 'Group',
  'sidebar.notifications.on': 'Notifications on',
  'sidebar.notifications.denied': 'Notifications blocked by the browser',
  'sidebar.notifications.enable': 'Enable notifications',

  // -- conversation ----------------------------------------------------------
  'thread.back': 'Back to conversations',
  'thread.typing': '{count, plural, one {{names} is typing…} other {{names} are typing…}}',
  'thread.members': '{count, plural, one {# member} other {# members}}',
  'thread.verifySafety': 'Verify safety number',
  'thread.encrypted': 'Messages in this conversation are end-to-end encrypted. Not even the server can read them.',
  'thread.replyingTo': 'Replying to: {preview}',
  'thread.attachment': 'Attachment',
  'thread.cancelReply': 'Cancel reply',
  'thread.recording': 'Recording… release to send',
  'thread.cancelRecording': 'Cancel recording',
  'thread.composer': 'Message',
  'thread.send': 'Send',
  'thread.voice.unavailable.title':
    'Voice recording needs HTTPS — the browser blocks the microphone on this address',
  'thread.voice.unavailable.label': 'Voice recording unavailable without HTTPS',
  'thread.voice.start': 'Record a voice message',
  'thread.voice.stop': 'Stop and send',

  // -- message bubble --------------------------------------------------------
  'bubble.deleted': 'This message was deleted',
  'bubble.actions': 'Message actions',
  'bubble.reply': 'Reply',
  'profile.profiles': 'Profiles',
  'profile.profiles.explain': 'Separate accounts on this device — work, personal, a business. All of them stay connected, so a message to any one of them notifies you.',
  'profile.profiles.add': 'Add a profile',
  'profile.profiles.create': 'Create',
  'profile.profiles.namePlaceholder': 'Work, Personal…',
  'profile.profiles.unnamed': 'Unnamed profile',
  'profile.profiles.remove': 'Remove profile',
  'profile.profiles.removeConfirm': 'Delete this account for good?',
  'profile.profiles.warning': 'A new profile is a new account with its own username — not another view of this one. It cannot be recovered either.',
  'profile.build': 'Build {build}',
  'profile.name.edit': 'Change name',
  'profile.name.save': 'Save',
  'profile.name.cancel': 'Cancel',
  'profile.name.failed': 'The name could not be changed. Check your connection and try again.',
  'profile.delete': 'Delete account',
  'profile.delete.explain': 'This removes your account from the server for good. Your keys exist only here, so there is no way back and no way to recover anything.',
  'profile.delete.others': 'Messages you already sent stay on the devices that received them. Nobody is told you left — they find out if they write to you.',
  'profile.delete.confirmPrompt': 'Type your username to confirm',
  'profile.delete.confirm': 'Delete for good',
  'profile.delete.failed': 'The account could not be deleted. Check your connection and try again.',
  'thread.recipientGone': 'This person is no longer on VAV. Your messages will not reach them.',
  'thread.recipientGone.title': 'This account was deleted',
  'profile.avatar.change': 'Change picture',
  'profile.avatar.remove': 'Remove picture',
  'profile.avatar.explain': 'Your picture is encrypted and sent only to people you talk to. Strangers see your initials.',
  'avatar.crop.title': 'Adjust your picture',
  'avatar.crop.hint': 'Drag to position, slide to zoom. What is inside the circle is what everyone sees.',
  'avatar.crop.zoom': 'Zoom',
  'avatar.crop.loadFailed': 'This picture could not be read. It may be a format the app does not support — try a different picture.',
  'call.incoming.audio': 'Incoming voice call',
  'call.incoming.video': 'Incoming video call',
  'call.accept': 'Accept',
  'call.decline': 'Decline',
  'call.outgoing': 'Ringing…',
  'call.cancel': 'Cancel',
  'call.hangup': 'End call',
  'call.mute': 'Mute',
  'call.unmute': 'Unmute',
  'call.cameraOff': 'Camera off',
  'call.cameraOn': 'Camera on',
  'call.connecting': 'Connecting…',
  'call.ended.declined': 'Call declined',
  'call.ended.busy': 'Busy',
  'call.ended.hangup': 'Call ended',
  'call.ended.failed': 'Could not start the call',
  'call.ended.unanswered': 'No answer',
  'thread.call.audio': 'Voice call',
  'thread.call.video': 'Video call',
  'call.legFailed': 'Could not connect',
  'call.turnMissing': 'The relay server (TURN) is not set up yet — calls between different networks need it',
  'call.forProfile': 'To your profile {name}',
  'call.toSpeaker': 'Speaker',
  'call.toEarpiece': 'Earpiece',
  'onboarding.link.start': 'I already have an account — link this device',
  'onboarding.link.title': 'Link this device',
  'onboarding.link.hint': 'On a device that already has your account, open My details, choose Link a new device, and scan this code or paste it.',
  'onboarding.link.verifyHint': 'Make sure these digits appear on the approving device too.',
  'onboarding.link.copy': 'Copy linking code',
  'onboarding.link.copied': 'Copied ✓',
  'onboarding.link.waiting': 'Waiting for approval…',
  'onboarding.link.failed': 'Linking did not complete. Start again on both devices.',
  'profile.device.remove': 'Disconnect',
  'profile.logout': 'Log out on this device',
  'profile.logout.explain': 'Your account stays on your other devices. Everything stored on this device is erased from it.',
  'profile.logout.confirm': 'Log out',
  'link.approve.stranded': 'This browser does not hold your account, so it cannot approve the new device. Open the link (or paste the code) in the VAV app on the device where your account lives.',
  'link.approve.title': 'Approve a new device',
  'link.approve.account': 'The new device will join the account {name}.',
  'profile.link.start': 'Link a new device',
  'profile.link.hint': 'Paste the linking code shown on the new device.',
  'profile.link.verifyHint': 'Approve only if the same digits are shown on the new device.',
  'profile.link.approve': 'Approve this device',
  'profile.link.done': 'Device linked',
  'profile.link.failed': 'Could not approve — the code may have expired.',
  'thread.date.today': 'Today',
  'thread.date.yesterday': 'Yesterday',
  'contact.delete': 'Delete contact',
  'contact.delete.explain': 'The conversation and its history are removed from this device only. They are not told, and their copy stays theirs.',
  'group.leave': 'Leave group',
  'group.leave.explain': 'The members will see that you left. The group and its history are removed from this device.',
  'contact.title': 'Contact details',
  'contact.verified': 'Verified',
  'profile.title': 'My details',
  'profile.username': 'Username',
  'profile.thisDevice': 'This device',
  'profile.devices': 'Devices',
  'profile.devicesLoading': 'Loading…',
  'profile.devicesNone': 'Could not reach the server.',
  'profile.deviceCurrent': 'this one',
  'profile.install': 'Install on this device',
  'profile.install.action': 'Add to home screen',
  'profile.install.already': 'You are using the installed app.',
  'profile.install.ios': 'Open the Share menu and choose “Add to Home Screen”.',
  'profile.install.unavailable': 'Your browser is not offering an install right now — usually because the app is already installed. Look for VAV in your app list.',
  'profile.noRecovery': 'Your keys exist only on this device. Clearing browser data or losing the phone loses the account — there is no recovery.',
  'bubble.forward': 'Forward',
  'bubble.select': 'Select',
  'bubble.moreReactions': 'More reactions',
  'select.cancel': 'Cancel selection',
  'select.count': '{count, plural, =0 {Select messages} one {# selected} other {# selected}}',
  'forward.title': 'Forward to',
  'forward.send': 'Send {count, plural, one {# message} other {# messages}} to {to, plural, one {# chat} other {# chats}}',
  'thread.voice.blocked.how': 'Open vav.chat in the Chrome browser (not the installed app), tap the icon left of the address bar → Permissions → Reset permissions. Then press the microphone again and approve.',
  'thread.voice.retry': 'Try again',
  'thread.voice.blockedByOs': 'This site is allowed to use the microphone, so the block is not here — the browser itself has no microphone permission from the phone.',
  'thread.voice.blockedByOs.how': 'Android: Settings → Apps → Chrome (or VAV, if you use the installed app) → Permissions → Microphone → Allow.',
  'thread.voice.sendFailed': 'The recording could not be sent. Check your connection and try again.',
  'thread.voice.busy': 'The microphone is allowed but could not be opened. Another app is probably using it.',
  'thread.voice.busy.how': 'Close anything that might hold it — a call, a voice assistant, a recorder — then try again. A restart of the phone clears a stuck one.',
  'thread.unreadFrom': '{count, plural, one {# new message} other {# new messages}}',
  'thread.voice.blocked': 'The microphone is blocked for this site. A refused permission cannot be asked for again — it has to be changed in settings.',
  'thread.voice.noMicrophone': 'No microphone was found on this device.',
  'push.prompt.title': 'Notifications are off',
  'push.prompt.body': 'Turn them on and your phone will tell you when a message arrives, even with the app closed.',
  'push.prompt.denied': 'This site is blocked from sending notifications. Allow it in your browser settings for the site, then come back.',
  'push.prompt.enable': 'Turn on',
  'push.prompt.dismiss': 'Not now',
  'quote.you': 'You',
  'quote.them': 'Them',
  'quote.unavailable': 'Message unavailable',
  'quote.image': 'Photo',
  'quote.video': 'Video',
  'quote.audio': 'Audio',
  'quote.voice': 'Voice message',
  'schedule.title': 'Send later',
  'schedule.explain': 'The message is written and sealed now, and leaves at the time you choose. Until then you can cancel it from its ⋯ menu.',
  'schedule.confirm': 'Schedule',
  'bubble.cancelScheduled': 'Cancel scheduled send',
  'bubble.state.scheduled': 'Scheduled',
  'profile.checkUpdate': 'Check for update',
  'profile.checkUpdate.busy': 'Checking…',
  'profile.checkUpdate.current': 'You are on the latest version.',
  'bubble.edit': 'Edit',
  'bubble.edited': 'edited',
  'thread.editing': 'Editing message',
  'group.edit.title': 'Group details',
  'group.edit.name': 'Group name',
  'bubble.deleteForMe': 'Delete for me',
  'bubble.save': 'Save image',
  'bubble.closeImage': 'Close image',
  'bubble.deleteForEveryone': 'Delete for everyone',
  'bubble.decryptFailed': 'This attachment could not be decrypted.',
  'bubble.decrypting': 'Decrypting…',
  'bubble.state.pending': 'Pending',
  'bubble.state.sent': 'Sent',
  'bubble.state.delivered': 'Delivered',
  'bubble.state.read': 'Read',
  'bubble.state.failed': 'Failed',

  // -- attach menu -----------------------------------------------------------
  'attach.label': 'Attach',
  'attach.camera': 'Camera',
  'attach.image': 'Photo',
  'attach.video': 'Video',
  'attach.audio': 'Audio',
  'attach.document': 'Document',

  // -- media players ---------------------------------------------------------
  'media.play': 'Play',
  'media.pause': 'Pause',
  'media.seek': 'Position in recording',
  'media.decrypting': 'Decrypting…',

  // -- invite dialog ---------------------------------------------------------
  'invite.title': 'Invite friends',
  'invite.close': 'Close',
  'invite.body':
    'Send the link. Whoever opens it will see that you invited them, and your conversation will be waiting once they sign up.',
  'invite.preparing': 'Preparing a link…',
  'invite.share': 'Share',
  'invite.copy': 'Copy link',
  'invite.copied': 'Copied',
  'invite.copyFailed': 'Could not copy — select the link and copy it manually',
  'invite.caution':
    'The link is valid for 30 days and can be reused. Anyone holding it can sign up and see that you invited them — do not post it publicly.',
  'invite.message': '{name} is inviting you to VAV — end-to-end encrypted messages.',
  'invite.defaultName': 'A friend',

  // -- new conversation ------------------------------------------------------
  'newChat.title': 'New conversation',
  'newChat.search': 'Username, full name or phone number',
  'newChat.direct': 'Chat',
  'newChat.group': 'Group',
  'newChat.groupName': 'Group name',
  'newChat.pickHint': 'Choose participants from the results — as many as you like.',
  'newChat.searching': 'Searching…',
  'newChat.noResults': 'No users found with that name.',
  'newChat.createGroup': 'Create group',
  'newChat.openChat': 'Open conversation',

  // -- phone discovery -------------------------------------------------------
  'phone.title': 'Phone number',
  'phone.privacy':
    'Your number is hashed on your device before it is sent. The server stores only the fingerprint, never the number.',
  'phone.mine': 'My number — so people can find me',
  'phone.save': 'Save',
  'phone.saved': 'Saved',
  'phone.findFriends': 'Find friends',
  'phone.fromContacts': 'Choose from contacts',
  'phone.noContactPicker':
    'This browser does not allow access to contacts. Type a number to check, or use Chrome on Android.',
  'phone.checkOne': 'Check a single number',
  'phone.search': 'Search',
  'phone.noneRegistered': 'None of those numbers are registered.',
  'phone.invalid': 'That does not look like a phone number',

  // -- safety number ---------------------------------------------------------
  'safety.title': 'Safety number',
  'safety.compare':
    'Compare this number with {name} over another channel — in person, or a phone call. If the numbers match, nobody is in the middle.',
  'safety.confirm': 'I verified this number',
  'safety.changeNote':
    'If {name} reinstalls the app, the number will change and you will be notified. An unexpected change is worth checking before you keep sending.',
  'safety.noSession': 'No keys have been exchanged with {name} yet. Send one message and come back.',
  // -- shared across clients --------------------------------------------------
  'common.cancel': 'Cancel',

  // -- native-only surfaces ---------------------------------------------------
  'push.channel': 'Messages',
  'push.newMessage': 'New message',
  'app.identityAlert.title': 'Identity key changed',
  'media.permission.library': 'No permission to access the photo library',
  'media.permission.camera': 'No permission to access the camera',
  'media.permission.microphone': 'No permission to access the microphone',
  'attach.error.title': 'Could not attach',
  'thread.voice.error.title': 'Could not record',
  'newChat.searchByUsername': 'Search by username',
  'language.title': 'Language',
  'language.note': 'Detected from your browser. Change it here if that guess is wrong.',
  'language.restart': 'Restart the app to finish switching layout direction.',
  'app.failed.title': 'Could not start',
  'app.failed.retry': 'Try again',
  'phone.invalidHint': 'Try the full international form, starting with your country code.',
  'phone.country': 'Country',
  'phone.national': 'Your number, as you would dial it locally',
  'phone.willSave': 'Will be saved as {number}',
  'phone.searchCountry': 'Search country',
  'phone.importContacts': 'Search my contacts',
  'phone.importing': 'Checking your contacts…',
  // Each number is hashed on this device at a deliberately high cost, so a large
  // address book takes a while. Saying where it has got to is the difference
  // between "slow" and "broken".
  'phone.importingProgress': 'Checking your contacts… {done} of {total}',
  'phone.importScanning': 'Reading your contacts…',
  // The gap between the two counts is usually large: an address book is full of
  // duplicates and entries that are not phone numbers, and only the unique valid
  // ones cost anything. Showing both is what makes the wait make sense.
  'phone.importScanned':
    '{entries} entries, {unique, plural, one {# number to check} other {# numbers to check}}',
  'phone.contactsDenied': 'Permission to read contacts was declined. You can still check a number by hand.',
  'phone.matched': '{count, plural, =0 {No one from your contacts is here yet} one {# contact is here} other {# contacts are here}}',
  'phone.contactsPrivacy': 'Your contacts are hashed on this device. The address book itself is never uploaded.',
  'invite.share.subject': 'Join me on VAV',
} as const;

export type MessageKey = keyof typeof en;
